import { tagsByGroupQueryOptions } from "@/api/Templates";
import Select from "@medoma/components/Select/Select";
import { Trans, useLingui } from "@lingui/react/macro";
import { Loading } from "@medoma/components/Loading/Loading";
import { tagGroupsSchema } from "@medoma/models/templates";
import { useQuery } from "@tanstack/react-query";
import { useFormContext } from "react-hook-form";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";
import { parseGroupDisplayName, parseGroupPatientType } from "../tagUtils";
import type { IPatientType } from "@medoma/models/patients";

export const NO_GROUP_ID = "no-group" as const;
export const NO_PATIENT_TYPE_SELECTED = "no-patient-type-selected" as const;

export type GroupSelectFields = {
  groupId: string;
};

type GroupSelectProps = {
  selectedPatientType: IPatientType | undefined;
};

export const GroupSelect = ({ selectedPatientType }: GroupSelectProps) => {
  const {
    formState: { errors },
    register,
  } = useFormContext<GroupSelectFields>();
  const {
    data: groups,
    isPending: isPendingTags,
    isError: isErrorTags,
    error: errorTags,
  } = useQuery(tagsByGroupQueryOptions(tagGroupsSchema.enum.group));
  const { t } = useLingui();

  // Groups are patient-type specific, so we intentionally show an empty list
  // until a patient type is selected, rather than showing an unfiltered mix.
  const filteredGroups =
    !groups || !selectedPatientType
      ? []
      : groups.filter(
          (group) =>
            parseGroupPatientType(group.displayName) === selectedPatientType,
        );

  if (isPendingTags) {
    return <Loading message={t`Hämtar grupper`} />;
  }

  if (isErrorTags) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta grupper.`} ${deducedError(errorTags)}`}
      />
    );
  }

  return (
    <Select
      {...register("groupId" as Parameters<typeof register>[0], {
        required: {
          value: true,
          message: t`Välj en grupp eller "Ingen"`,
        },
      })}
      label={t`Grupp`}
      errorMessage={errors.groupId?.message}
    >
      {!selectedPatientType ? (
        <option disabled value={NO_PATIENT_TYPE_SELECTED}>
          <Trans>Välj typ av patient för att välja en grupp</Trans>
        </option>
      ) : (
        <>
          <option value={NO_GROUP_ID}>
            <Trans>Ingen</Trans>
          </option>
          {filteredGroups.map((group) => (
            <option key={group.id} value={group.id}>
              {parseGroupDisplayName(group.displayName)}
            </option>
          ))}
        </>
      )}
    </Select>
  );
};
